import { useEffect, useMemo, type ReactNode } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CUSTOMER_TEST_REQUEST_STATUSES } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types/customer";
import { REQUEST_STATUS_LABEL_KEY } from "@/components/my-requests/request-status";
import { TestingHeader } from "@/components/testing/testing-header";
import {
  buttonClasses,
  Card,
  EmptyState,
  FilterBar,
  FormField,
  LoadError,
  Pagination,
  RequestCard,
  Select,
  Skeleton,
} from "@/components/ui";
import { useAccountDevices } from "@/hooks/use-account-devices";
import { REQUEST_PAGE_LIMIT, useCustomerTestRequests } from "@/hooks/use-customer-test-requests";
import { useForeignPropertyReset } from "@/hooks/use-foreign-property-reset";
import { requestLink, requestTestLink } from "@/lib/destinations";
import {
  filterRequests,
  findRequestByThread,
  requestDeviceLabel,
  validateRequestsSearch,
} from "@/lib/request-list";

type CustomerTestRequest = components["schemas"]["CustomerTestRequest"];

export const Route = createFileRoute("/_customer/testing/requests/")({
  validateSearch: validateRequestsSearch,
  component: RequestsPage,
});

const PAGE_SIZE = 20;

const CARDS = "grid gap-3 lg:grid-cols-2";

const NO_REQUESTS: readonly CustomerTestRequest[] = [];

// The cards' shape in static blocks: the device, the property, the chip and the day.
const LOADING = (
  <div className={CARDS}>
    {[0, 1, 2, 3].map((card) => (
      <div key={card} className="rounded-card border border-line bg-surface p-4 shadow-card lg:p-5">
        <Skeleton width="60%" />
        <Skeleton width="40%" height={12} className="mt-2" />
        <Skeleton width={128} height={24} className="mt-3 rounded-full" />
        <Skeleton width="35%" height={12} className="mt-3" />
      </div>
    ))}
  </div>
);

/**
 * My requests: the active account's test requests newest first, as cards
 * paged twenty at a time, narrowed by property and status. The feed takes no
 * account, so it is narrowed to the account's facilities here; the emailed
 * `?thread=` link is resolved over the whole feed instead, since it may name a
 * request at any of the person's accounts, and opens that request's record.
 * The feed is one page of the newest requests, so a miss there asks the server
 * for the request owning the thread; only an answered empty lookup says the
 * conversation was not found.
 */
function RequestsPage() {
  const { t } = useTranslation("customer");
  const navigate = useNavigate();
  const { property, status, thread, page } = Route.useSearch();
  const devices = useAccountDevices();
  const { installations, facilities, facilitiesById } = devices;
  useForeignPropertyReset(property, devices.isPending || devices.isError ? undefined : facilities);

  const params = useMemo(
    () =>
      property === undefined
        ? { limit: REQUEST_PAGE_LIMIT }
        : { limit: REQUEST_PAGE_LIMIT, facility_id: property },
    [property],
  );
  const read = useCustomerTestRequests(params);
  const items = read.data?.items;

  const feedArrival = thread !== undefined && items ? findRequestByThread(items, thread) : null;
  const feedMiss = thread !== undefined && items !== undefined && feedArrival === null;
  // The lookup takes no property: the link may name a request at any account.
  const threadParams = useMemo(() => ({ thread_id: thread, limit: 1 }), [thread]);
  const threadRead = useCustomerTestRequests(threadParams, { enabled: feedMiss });
  const threadAnswer = feedMiss && threadRead.isSuccess ? threadRead.data.items : undefined;
  const arrival = feedArrival ?? threadAnswer?.[0] ?? null;
  const arrivalId = arrival?.id;
  useEffect(() => {
    if (arrivalId) {
      void navigate({
        to: "/testing/requests/$requestId",
        params: { requestId: arrivalId },
        replace: true,
      });
    }
  }, [arrivalId, navigate]);

  const facilityIds = useMemo(() => new Set(facilities.map((f) => f.id)), [facilities]);
  const installationsById = useMemo(
    () => new Map(installations.map((installation) => [installation.id, installation])),
    [installations],
  );
  const matching = useMemo(
    () => filterRequests(items ?? NO_REQUESTS, { facilityIds, property, status }),
    [items, facilityIds, property, status],
  );
  const propertyOptions = useMemo(
    () =>
      [...facilities]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((facility) => ({ value: facility.id, label: facility.name })),
    [facilities],
  );

  // Every write passes through the validator, so the URL holds what the page
  // reads back: an empty choice and the first page are the param's absence.
  // A filter change names neither the thread nor the page, so both drop.
  const onSearch = (next: Record<string, unknown>) =>
    void navigate({ to: "/testing/requests", search: validateRequestsSearch(next) });

  const filtered = property !== undefined || status !== undefined;
  const currentPage = page ?? 1;
  const visible = matching.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const threadMissing = threadAnswer?.length === 0;
  const truncated = read.data && read.data.total_count > read.data.items.length ? read.data : null;

  let body: ReactNode;
  if (read.isError || devices.isError) {
    body = (
      <LoadError
        title={t("requests.error")}
        body={t("loadError.body")}
        onRetry={() => {
          void read.refetch();
          devices.refetch();
        }}
      />
    );
  } else if (read.isPending || devices.isPending || arrival) {
    body = LOADING;
  } else if (visible.length > 0) {
    body = (
      <div>
        <ul className={CARDS}>
          {visible.map((request) => (
            <li key={request.id}>
              <RequestCard
                request={request}
                link={requestLink(request)}
                deviceLabel={requestDeviceLabel(
                  installationsById.get(request.device_installation_id ?? ""),
                )}
                propertyName={facilitiesById.get(request.facility_id)?.name}
              />
            </li>
          ))}
        </ul>
        {matching.length > PAGE_SIZE && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(matching.length / PAGE_SIZE)}
            totalItems={matching.length}
            pageSize={PAGE_SIZE}
            onPageChange={(next) => onSearch({ property, status, page: next })}
          />
        )}
      </div>
    );
  } else if (filtered || page !== undefined) {
    // A page past the last one reads as no match too, and Reset returns to the first.
    body = (
      <Card>
        <EmptyState
          variant="inline"
          title={t("requests.noMatch")}
          actionLabel={t("filters.reset")}
          onAction={() => onSearch({})}
        />
      </Card>
    );
  } else {
    body = (
      <Card>
        <p className="text-sm font-medium text-ink">{t("requests.empty")}</p>
        <Link {...requestTestLink(null)} className={`mt-3 ${buttonClasses("primary")}`}>
          {t("requests.emptyAction")}
        </Link>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5 lg:gap-6">
      <TestingHeader active="requests" />
      <div className="flex flex-col gap-4">
        {threadMissing && (
          <p
            role="status"
            className="rounded-card border border-line bg-surface p-4 text-sm text-body shadow-card"
          >
            {t("requests.threadMissing")}
          </p>
        )}
        <FilterBar onReset={() => onSearch({})} canReset={filtered}>
          <div className="sm:w-56">
            <FormField label={t("requests.property")}>
              <Select
                value={property ?? ""}
                options={[{ value: "", label: t("requests.propertyAll") }, ...propertyOptions]}
                onChange={(event) => onSearch({ property: event.target.value, status })}
              />
            </FormField>
          </div>
          <div className="sm:w-56">
            <FormField label={t("requests.status")}>
              <Select
                value={status ?? ""}
                options={[
                  { value: "", label: t("requests.statusAll") },
                  ...CUSTOMER_TEST_REQUEST_STATUSES.map((value) => ({
                    value,
                    label: t(REQUEST_STATUS_LABEL_KEY[value]),
                  })),
                ]}
                onChange={(event) => onSearch({ property, status: event.target.value })}
              />
            </FormField>
          </div>
        </FilterBar>
        {truncated && !read.isError && (
          <p className="text-sm text-muted">
            {t("requests.truncated", {
              shown: truncated.items.length,
              total: truncated.total_count,
            })}
          </p>
        )}
        {body}
      </div>
    </div>
  );
}
